/**
 * DataService
 * Fetches Data from server, processes it.
 * Used by App.vue to load data into AppModel
 */

import _ from 'lodash';
import path from 'path';
import { makeReactive } from '@dha/make-reactive';
import config from './InputFileConfig';
import { camelCaseCsv } from './helpers';

const { json } = d3;
const { processFile, inputFileConfig } = config;

function getExtension(file) {
    return _(file).split('.').last();
}

async function fetchData(files) {
    const baseUrl = process.env.BASE_URL;
    // console.time('DataService::fetchData');
    const fileContents = await Promise.all(_.map(files, (async ({ file }, key) => {
        // console.info(baseUrl, 'data', file);
        const filePath = path.join('data', file);
        let fullPath = `${baseUrl}${filePath}`;
        // //Relative path:
        if (!baseUrl.startsWith('http')) {
            fullPath = path.join(baseUrl, 'data', file);
        }
        // console.info(fullPath);
        const extension = getExtension(file);
        try {
            switch (extension) {
            case 'json':
                return { key, contents: await json(fullPath) };
            case 'geojson':
                return { key, contents: await json(fullPath) };
            case 'csv':
                return { key, contents: await camelCaseCsv(fullPath) };
            default:
                throw new Error(`Unsupported file extension: ${extension}`);
            }
        } catch (err) {
            console.error(`Error loading ${fullPath}: ${err.message}`);
            console.error(err);
            throw err;
        }
    })));
    // console.timeEnd('DataService::fetchData');

    return _(fileContents)
        .keyBy('key')
        .mapValues('contents')
        .value();
}
/* eslint-disable no-param-reassign */
async function processData(data, fileConfigs) {
    // console.time('DataService::processData');

    // Extract standardized columns
    // console.time('DataService:: Standardize input columns');
    data = _.mapValues(data, (contents, file) => {
        const { columns } = fileConfigs[file];
        if (getExtension(file) === 'json' || getExtension(file) === 'geojson' || !columns) {
            return contents;
        }

        return contents.map(processFile({ columns }));
    });
    // console.timeEnd('DataService:: Standardize input columns');

    // console.time('DataService:: Generate data that should be in a file');
    const zoneLookup = _(data.postalLookup)
        .uniqBy(row => `${row.localCode}|${row.municipalityFullName}`)
        .map(row => _.pick(row, ['localCode', 'localName', 'municipalityFullName', 'zoneCode', 'zoneName']))
        .value();
    // console.timeEnd('DataService:: Generate data that should be in a file');
    // console.timeEnd('DataService::processData');
    return { zoneLookup, ...data };
}

class DataService {
    constructor() {
        this.status = {
            loaded: false,
            message: 'Initializing',
            progress: 0
        };
        this.data = {
            postalLookup: [],
            zoneLookup: [],
            lgaClassLookup: [],
            populationAgeGender: [],
            surveyAnswers: [],
            surveyQuestions: [],
            lgaGeojson: null,
            zonesGeojson: null
        };
    }

    async load() {
        try {
            this.status.message = 'Downloading';
            const rawData = await fetchData(inputFileConfig);

            this.status.message = 'Processing';
            const processedData = await processData(rawData, inputFileConfig);
            this.data = _.mapValues(processedData, obj => Object.freeze(obj));
            this.status.loaded = true;

            this.status.message = 'Done';
        } catch (err) {
            console.error(err);
            alert('Error loading data. See console for more details');
        }
    }
}

const dataService = makeReactive(new DataService());
dataService.load();
export default dataService;
